import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';
import { CheckoutActions } from '@app/pages/checkout-app/checkout-store/actions';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import {
  ExtensionComponentType,
  ICheckoutExtensions,
  ShippingRatesFilterExtension,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { generateRandomString } from '@utils/string-utils';
import { ViewMode } from '../../../models';
import { getExtensionRules } from '../../../shared/all-extension-rules';
import { ShopService } from '@services/shops/shop.service';
import { ExtensionsService } from '@app/pages/checkout-app/services/extensions/extensions.service';
@Component({
  selector: 'shipping-rates-filter',
  templateUrl: './shipping-rates-filter.component.html',
  styleUrls: ['./shipping-rates-filter.component.scss'],
  standalone: false,
})
@UntilDestroy()
export class ShippingRatesFilterComponent implements OnInit {
  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedShippingMethods$ = this.store.select(CheckoutReducer.selectShippingMethods);
  loadedExtension: ICheckoutExtensions;
  formGroup: UntypedFormGroup;
  restrictionsRules = getExtensionRules();
  entities = this.restrictionsRules.map((rule) => rule.entity);
  FilterType = ShippingRatesFilterExtension.FilterType;
  UncheckedBehavior = ShippingRatesFilterExtension.UncheckedBehavior;
  shopCurrencySymbol: string;
  checkoutGroupComment = 'For methods with the same name, we can’t identify which checkout group they are a part of';
  constructor(
    private store: Store<CheckoutReducer.State>,
    private rulesDynamicFormService: RulesDynamicFormService,
    public shopService: ShopService,
    private extensionsService: ExtensionsService,
  ) {}

  ngOnInit() {
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.SHIPPING_RATES_FILTER);
    this.extensionsService.save$.pipe(untilDestroyed(this)).subscribe(() => {
      this.handleSave();
    });

    this.store.dispatch(CheckoutActions.loadCheckouts());
    this.shopCurrencySymbol = this.shopService.shopCurrencySymbol;
    this.initForm();
    this.initSubscriptions();
  }

  handleSave() {
    const formValue = this.formGroup.value;
    const usageToken = formValue.usageToken;
    delete formValue.usageToken;
    const template = this.prepareFormValues(formValue);
    const payload: Partial<ICheckoutExtensions> = {
      template,
      usageToken,
      type: ExtensionComponentType.SHIPPING_RATES_FILTER,
    };

    if (this.loadedExtension?.id) {
      const id = this.loadedExtension.id;
      this.store.dispatch(CheckoutActions.updateExtension({ id, extension: { ...payload, id } }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: payload }));
    }
  }

  private initSubscriptions() {
    this.initLoadedExtensionSubscription();
    this.initValueChangesSubscription();
  }

  private initLoadedExtensionSubscription() {
    this.loadedExtension$.pipe(untilDestroyed(this)).subscribe((extension: ICheckoutExtensions) => {
      this.loadedExtension = extension;
      if (extension) {
        this.initForm(extension);
      }
    });
  }

  private initValueChangesSubscription() {
    this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe((values) => {
      this.dispatchExampleViewValues(values);
    });
  }

  private filterValidator(): ValidatorFn {
    return (filterGroup: UntypedFormGroup) => {
      const filterType = filterGroup.get('type')?.value;
      const subTextControl = filterGroup.get('subText');
      const methodListControl = filterGroup.get('methodNames');
      let errors: {} | null = null;

      if (filterType === ShippingRatesFilterExtension.FilterType.SUB_TEXT) {
        errors = subTextControl?.value ? null : { required: true };
        subTextControl.setErrors(errors);
        methodListControl.setErrors(null);
      } else {
        errors = methodListControl?.value?.length ? null : { required: true };
        methodListControl.setErrors(errors);
        subTextControl.setErrors(null);
      }

      return errors;
    };
  }

  private prepareFormValues(values) {
    const template = {
      ...values,
    } as ShippingRatesFilterExtension.Template;

    if (!template.config.bypassOptions.enableSpecificMethodBypass) {
      delete template.config.bypassOptions.bypassedMethodNames;
    }

    template.config.filter.type === this.FilterType.SUB_TEXT
      ? delete template.config.filter.methodNames
      : delete template.config.filter.subText;

    return template;
  }

  private dispatchExampleViewValues(values) {
    if (!values) {
      return;
    }

    const template = this.prepareFormValues(values);
    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    const data = {
      [ExtensionComponentType.SHIPPING_RATES_FILTER]: {
        mode,
        template,
      },
    };
    this.store.dispatch(CheckoutActions.liveExampleTemplateView({ data }));
  }

  private bypassOptionsValidator() {
    return (bypassOptionsGroup: UntypedFormGroup) => {
      const optionChecked = bypassOptionsGroup.get('enableSpecificMethodBypass')?.value;
      const methodNamesControl = bypassOptionsGroup.get('bypassedMethodNames');
      let errors: {} | null = null;
      if (!optionChecked) {
        methodNamesControl.setErrors(null);
      } else {
        errors = methodNamesControl?.value?.length ? null : { required: true };
        methodNamesControl.setErrors(errors);
      }

      return errors;
    };
  }

  private initForm(extension?: ICheckoutExtensions) {
    const templateValue = extension?.template ?? {};

    this.formGroup = new UntypedFormGroup({
      usageToken: new UntypedFormControl(extension?.usageToken || 'shipping_rates_filter_' + generateRandomString(12)),
      title: new UntypedFormControl(templateValue?.title ?? 'Shipping rates filter', Validators.required),
      internalName: new UntypedFormControl(templateValue?.internalName ?? ''),
      restrictionsRules: this.rulesDynamicFormService.initRulesFormArray(
        templateValue?.restrictionsRules ?? [],
        getExtensionRules(),
      ),
      config: new UntypedFormGroup({
        checkboxText: new UntypedFormControl(
          templateValue?.config?.checkboxText ?? 'Checkbox text',
          Validators.required,
        ),
        checkboxHelpText: new UntypedFormControl(templateValue?.config?.checkboxHelpText ?? 'Checkbox help text'),
        filter: new UntypedFormGroup(
          {
            type: new UntypedFormControl(templateValue?.config?.filter?.type ?? this.FilterType.SUB_TEXT),
            subText: new UntypedFormControl(templateValue?.config?.filter?.subText ?? ''),
            methodNames: new UntypedFormControl(templateValue?.config?.filter?.methodNames ?? []),
          },
          this.filterValidator(),
        ),
        uncheckedBehavior: new UntypedFormControl(
          templateValue?.config?.uncheckedBehavior ?? this.UncheckedBehavior.EXCLUDE_CONFIGURED_METHODS,
        ),
        bypassOptions: new UntypedFormGroup(
          {
            enableFreeMethodBypass: new UntypedFormControl(
              templateValue?.config?.bypassOptions?.enableFreeMethodBypass ?? false,
            ),
            enableSpecificMethodBypass: new UntypedFormControl(
              templateValue?.config?.bypassOptions?.enableSpecificMethodBypass ?? false,
            ),
            bypassedMethodNames: new UntypedFormControl(
              templateValue?.config?.bypassOptions?.bypassedMethodNames ?? [],
            ),
          },
          this.bypassOptionsValidator(),
        ),
        defaultCheckboxState: new UntypedFormControl(templateValue?.config?.defaultCheckboxState ?? true),
      }),
    });

    this.dispatchExampleViewValues(this.formGroup.value);
    this.formGroup.statusChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.extensionsService.setFormValid(this.formGroup.valid);
    });
    this.extensionsService.setFormValid(this.formGroup.valid);
  }
}
